/**
 * Client session storage using first-party cookies (Secure + SameSite=Lax when possible).
 *
 * Large values (e.g. encrypted userData) are kept out of cookies so they are not sent
 * on every document request — oversized Cookie headers cause HTTP 431 on refresh in dev.
 *
 * userData is dual-written to sessionStorage (current tab) and localStorage (shared across
 * tabs) so opening a new tab still has Modules/permissions for the sidebar menu.
 *
 * Note: JavaScript cannot set HttpOnly; XSS could still read these stores. True HttpOnly
 * session cookies require the API to issue Set-Cookie on login and validate on each request.
 */

const PREFIX = 'aq_';
/** Stay under typical 4KB per-cookie limits after URI encoding. */
const MAX_CHUNK = 3000;
/** Scan for orphan chunk cookies left from older sessions. */
const MAX_CHUNK_INDEX_SCAN = 48;

/** Never chunk these into cookies — use sessionStorage + localStorage instead. */
const SESSION_STORAGE_KEYS = new Set(['userData']);

export const AUTH_SESSION_KEYS = [
  'sessionToken',
  'userData',
  'isLoggedIn',
  'lastActivity',
  'sessionStart',
  'sessionTimeout',
  'userName',
  'userRole',
  'organizationName',
  'staffId',
  'organizationId',
  'credentialGate',
];

function isSecureContext() {
  if (typeof window === 'undefined') return false;
  return (
    window.location.protocol === 'https:' ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'
  );
}

function cookieAttrs(maxAgeSec) {
  let attrs = 'path=/; SameSite=Lax';
  if (isSecureContext()) attrs += '; Secure';
  attrs += `; Max-Age=${maxAgeSec}`;
  return attrs;
}

function storageKey(key) {
  return `${PREFIX}${key}`;
}

function readSessionStorageAuth(key) {
  try {
    return sessionStorage.getItem(storageKey(key));
  } catch {
    return null;
  }
}

function writeSessionStorageAuth(key, value) {
  try {
    sessionStorage.setItem(storageKey(key), value);
  } catch {
    // ignore quota / private mode
  }
}

function removeSessionStorageAuth(key) {
  try {
    sessionStorage.removeItem(storageKey(key));
  } catch {
    // ignore
  }
}

function readLocalStorageAuth(key) {
  try {
    return localStorage.getItem(storageKey(key));
  } catch {
    return null;
  }
}

function writeLocalStorageAuth(key, value) {
  try {
    localStorage.setItem(storageKey(key), value);
  } catch {
    // ignore quota / private mode
  }
}

function removeLocalStorageAuth(key) {
  try {
    localStorage.removeItem(storageKey(key));
  } catch {
    // ignore
  }
}

/** Write large auth payloads for this tab and for other tabs. */
function writeLargeAuth(key, value) {
  writeSessionStorageAuth(key, value);
  writeLocalStorageAuth(key, value);
}

function getCookieRaw(name) {
  if (typeof document === 'undefined') return null;
  const parts = document.cookie.split(';');
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i].trim();
    const eq = p.indexOf('=');
    if (eq === -1) continue;
    const k = p.slice(0, eq);
    if (k === name) return p.slice(eq + 1);
  }
  return null;
}

function deleteChunked(key) {
  document.cookie = `${PREFIX}${key}=; ${cookieAttrs(0)}`;
  document.cookie = `${PREFIX}${key}_n=; ${cookieAttrs(0)}`;
  for (let i = 0; i < MAX_CHUNK_INDEX_SCAN; i += 1) {
    document.cookie = `${PREFIX}${key}__${i}=; ${cookieAttrs(0)}`;
  }
}

function shouldUseSessionStorage(key, encodedLength) {
  return SESSION_STORAGE_KEYS.has(key) || encodedLength > MAX_CHUNK;
}

function decodeCookieValue(raw) {
  if (raw == null || raw === '') return null;
  try {
    return decodeURIComponent(raw);
  } catch {
    return null;
  }
}

function readAuthValueFromCookiesOnly(key) {
  const nRaw = getCookieRaw(`${PREFIX}${key}_n`);
  if (nRaw != null && nRaw !== '') {
    const n = parseInt(nRaw, 10) || 0;
    if (n > 0) {
      let enc = '';
      for (let i = 0; i < n; i += 1) {
        const part = getCookieRaw(`${PREFIX}${key}__${i}`);
        if (part == null) return null;
        enc += part;
      }
      return decodeCookieValue(enc);
    }
  }
  return decodeCookieValue(getCookieRaw(`${PREFIX}${key}`));
}

function migrateLargeAuthFromCookies(key, value) {
  if (!value) return;
  writeLargeAuth(key, value);
  deleteChunked(key);
}

/**
 * @param {string} key
 * @param {string} value
 * @param {number} [maxAgeSec=86400] default 24h (aligned with client session token age)
 */
export function setAuthItem(key, value, maxAgeSec = 86400) {
  if (value == null) {
    removeAuthItem(key);
    return;
  }
  const str = String(value);
  const enc = encodeURIComponent(str);

  if (shouldUseSessionStorage(key, enc.length)) {
    deleteChunked(key);
    writeLargeAuth(key, str);
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
    return;
  }

  removeSessionStorageAuth(key);
  removeLocalStorageAuth(key);
  deleteChunked(key);
  document.cookie = `${PREFIX}${key}=${enc}; ${cookieAttrs(maxAgeSec)}`;
}

/**
 * Read value; migrates from localStorage / cookies / legacy localStorage if present.
 * @param {string} key
 * @returns {string|null}
 */
export function getAuthItem(key) {
  // Prefer tab session, then shared localStorage (new tabs / oversized payloads)
  const fromSession = readSessionStorageAuth(key);
  if (fromSession != null && fromSession !== '') {
    if (SESSION_STORAGE_KEYS.has(key) && !readLocalStorageAuth(key)) {
      writeLocalStorageAuth(key, fromSession);
    }
    return fromSession;
  }

  const fromShared = readLocalStorageAuth(key);
  if (fromShared != null && fromShared !== '') {
    if (SESSION_STORAGE_KEYS.has(key)) {
      writeSessionStorageAuth(key, fromShared);
    }
    return fromShared;
  }

  const fromCookies = readAuthValueFromCookiesOnly(key);
  if (fromCookies != null && fromCookies !== '') {
    if (
      SESSION_STORAGE_KEYS.has(key) ||
      encodeURIComponent(fromCookies).length > MAX_CHUNK
    ) {
      migrateLargeAuthFromCookies(key, fromCookies);
    }
    return fromCookies;
  }

  try {
    const legacy = localStorage.getItem(key);
    if (legacy != null) {
      setAuthItem(key, legacy);
      localStorage.removeItem(key);
      return legacy;
    }
  } catch {
    // ignore
  }
  return null;
}

export function removeAuthItem(key) {
  deleteChunked(key);
  removeSessionStorageAuth(key);
  removeLocalStorageAuth(key);
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

export function clearAuthSession() {
  AUTH_SESSION_KEYS.forEach((k) => removeAuthItem(k));
}

/**
 * Move large auth payloads out of cookies and drop orphan chunk cookies.
 * Also mirror session userData into localStorage so new tabs can load menus.
 * Call once when the SPA boots so the next full page refresh sends smaller headers.
 */
export function repairAuthSessionCookies() {
  if (typeof window === 'undefined') return;

  SESSION_STORAGE_KEYS.forEach((key) => {
    const fromSession = readSessionStorageAuth(key);
    if (fromSession) {
      writeLocalStorageAuth(key, fromSession);
      deleteChunked(key);
      return;
    }

    const fromShared = readLocalStorageAuth(key);
    if (fromShared) {
      writeSessionStorageAuth(key, fromShared);
      deleteChunked(key);
      return;
    }

    const fromCookies = readAuthValueFromCookiesOnly(key);
    if (fromCookies) {
      migrateLargeAuthFromCookies(key, fromCookies);
    }
  });
}
